// LT-356 — operator-driven enrolment modal with same-campaign override path.
//
// Two-step flow:
//
//   1. Pick a contact (search box → list).
//   2. Submit. On 201 → done. On 409 with `override_required: true`,
//      switch to the force-re-enrol step: capture an operator reason
//      and retry with `force_re_enroll=true`.
//
// The 409 body is the LT-356 service-layer block ("contact previously
// enrolled in this campaign"). The DB partial unique index on active
// rows still wins — a 409 without `override_required: true` ("contact
// already actively enrolled") is shown as a plain error and does NOT
// open the override path.
import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import { Button, Card, Input, Pill, Textarea } from '../../ui';
import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import { contactsAPI } from '../../services/contactsAPI';
import type { Contact } from '../../types/contact';

interface Props {
  campaignId: string;
  onClose: () => void;
  onEnrolled: () => void;
}

const CampaignEnrolContactModal: React.FC<Props> = ({
  campaignId,
  onClose,
  onEnrolled,
}) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Contact | null>(null);

  const [overrideRequired, setOverrideRequired] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Debounced contact search.
  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = window.setTimeout(() => {
      contactsAPI
        .list({ q: search.trim(), limit: 10 })
        .then((data) => {
          if (!cancelled) setResults(data);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [search]);

  const submit = async (force: boolean) => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      await campaignsAPI.enrol(campaignId, {
        contact_id: selected.id,
        ...(force
          ? {
              force_re_enroll: true,
              override_reason: overrideReason.trim(),
            }
          : {}),
      });
      onEnrolled();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        .response?.data?.detail;
      if (
        detail &&
        typeof detail === 'object' &&
        (detail as Record<string, unknown>).override_required === true
      ) {
        setOverrideRequired(true);
        const msg = (detail as { message?: unknown }).message;
        setErr(
          typeof msg === 'string'
            ? msg
            : 'This contact has already been enrolled in this campaign — explicit operator override required.',
        );
      } else {
        setErr(extractCampaignApiError(e, 'Enrolment failed.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const canSubmitForce =
    overrideRequired && overrideReason.trim().length > 0 && !busy;

  const headline = useMemo(
    () => (overrideRequired ? 'Force re-enrolment' : 'Enrol contact'),
    [overrideRequired],
  );

  const fullName = (c: Contact) =>
    [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email || c.id;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enrol-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md p-5">
        <h2
          id="enrol-modal-title"
          className="text-lg font-semibold text-slate-50 mb-3"
        >
          {headline}
        </h2>

        {!overrideRequired ? (
          <>
            <label className="block mb-3">
              <span className="text-xs text-slate-400">Search contacts</span>
              <div className="relative">
                <Search
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500"
                  size={14}
                  strokeWidth={2}
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type at least 2 characters…"
                  aria-label="Search contacts to enrol"
                  className="pl-7"
                />
              </div>
            </label>

            <div
              className="max-h-56 overflow-y-auto border border-white/[0.06] rounded-ds-md mb-3"
              data-testid="enrol-modal-results"
            >
              {searching ? (
                <div className="text-xs text-slate-500 px-3 py-2">Searching…</div>
              ) : results.length === 0 ? (
                <div className="text-xs text-slate-500 px-3 py-2">
                  {search.trim().length < 2
                    ? 'Start typing to search.'
                    : 'No contacts matched.'}
                </div>
              ) : (
                <ul role="listbox" aria-label="Contact results">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(c)}
                        aria-selected={selected?.id === c.id}
                        role="option"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-white/[0.04] ${
                          selected?.id === c.id
                            ? 'bg-accent-tint text-accent-text'
                            : 'text-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{fullName(c)}</span>
                          <Pill intent="neutral">{c.lifecycle_stage}</Pill>
                        </div>
                        {c.email ? (
                          <div className="text-xs text-slate-500 truncate">
                            {c.email}
                          </div>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-300 mb-3">
              <strong>{selected ? fullName(selected) : 'This contact'}</strong> has
              already been enrolled in this campaign. Re-enrolment is blocked by
              default — supply a reason below to override.
            </p>
            <label className="block mb-3">
              <span className="text-xs text-slate-400">
                Reason (recorded on the campaign audit log)
              </span>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={3}
                placeholder="e.g. contact re-engaged via inbound, …"
                aria-label="Override reason"
              />
            </label>
          </>
        )}

        {err ? (
          <div className="mb-3 px-3 py-2 rounded-ds-md border border-warn-border bg-warn-tint text-warn-text text-sm">
            {err}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button intent="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {overrideRequired ? (
            <Button
              intent="danger"
              onClick={() => submit(true)}
              disabled={!canSubmitForce}
            >
              Force enrol
            </Button>
          ) : (
            <Button
              intent="primary"
              onClick={() => submit(false)}
              disabled={!selected || busy}
            >
              Enrol
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CampaignEnrolContactModal;
